import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CustomTooltip } from './tooltip';
import { useMediaQuery } from 'react-responsive';
import dayjs from 'dayjs';

const AnswersByDateChart = memo(({ taskIdsList, dateRange }) => {
    const { t } = useTranslation();
    const isMobile = useMediaQuery({ maxWidth: 768 });
    const isTablet = useMediaQuery({ minWidth: 769, maxWidth: 1024 });

    const chartData = useMemo(() => {
        if (!taskIdsList || taskIdsList.length === 0) return [];

        // Analisa o range de datas para decidir agrupamento
        let startDate, endDate;
        if (dateRange && dateRange[0] && dateRange[1]) {
            startDate = dayjs(dateRange[0]);
            endDate = dayjs(dateRange[1]);
        } else {
            // Se não tem range, usa as datas das tarefas
            const dates = taskIdsList
                .map(t => t.endDate)
                .filter(Boolean)
                .map(d => dayjs(d))
                .filter(d => d.isValid());
            
            if (dates.length === 0) return [];
            
            startDate = dates.reduce((min, d) => d.isBefore(min) ? d : min, dates[0]);
            endDate = dates.reduce((max, d) => d.isAfter(max) ? d : max, dates[0]);
        }

        const daysDiff = endDate.diff(startDate, 'day');
        const groupByMonth = daysDiff > 90; // Se mais de 90 dias, agrupa por mês

        // Agrupa as tarefas por período
        const grouped = {};
        
        taskIdsList.forEach(task => {
            const taskDate = dayjs(task.endDate);
            if (!taskDate.isValid()) return;

            let key;
            if (groupByMonth) {
                // Agrupa por mês (YYYY-MM)
                key = taskDate.format('YYYY-MM');
            } else {
                // Agrupa por dia (YYYY-MM-DD)
                key = taskDate.format('YYYY-MM-DD');
            }

            grouped[key] = (grouped[key] || 0) + 1;
        });

        // Converte para array e ordena
        const data = Object.entries(grouped)
            .map(([date, count]) => ({
                name: groupByMonth 
                    ? dayjs(date).format('MMM YYYY')
                    : dayjs(date).format('DD/MM'),
                fullName: groupByMonth 
                    ? dayjs(date).format('MMMM YYYY')
                    : dayjs(date).format('DD/MM/YYYY'),
                value: count,
                date: date,
                percent: 0 // Será calculado depois
            }))
            .sort((a, b) => a.date.localeCompare(b.date));

        // Calcula percentuais
        const total = data.reduce((sum, item) => sum + item.value, 0);
        data.forEach(item => {
            item.percent = total > 0 ? (item.value / total) * 100 : 0;
        });

        return data;
    }, [taskIdsList, dateRange]);

    if (chartData.length === 0) {
        return null;
    }

    // Calcula intervalo dinâmico para labels do eixo X baseado no tamanho da tela
    const maxLabels = isMobile ? 8 : isTablet ? 12 : 20;
    const xAxisInterval = chartData.length > maxLabels ? Math.floor(chartData.length / maxLabels) : 0;

    // Dimensões responsivas
    const chartHeight = isMobile ? 300 : isTablet ? 280 : 250;
    const fontSize = isMobile ? 9 : isTablet ? 10 : 10;
    const yAxisFontSize = isMobile ? 10 : 11;
    const xAxisAngle = isMobile ? -60 : -45;
    const xAxisHeight = isMobile ? 80 : 60;
    const yAxisWidth = isMobile ? 40 : 50;

    return (
        <ResponsiveContainer width="100%" height={chartHeight}>
            <AreaChart 
                data={chartData} 
                margin={{ 
                    top: 20, 
                    right: isMobile ? 10 : 30, 
                    left: isMobile ? 10 : 20, 
                    bottom: isMobile ? 70 : 50 
                }}
            >
                <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#4085c6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#4085c6" stopOpacity={0}/>
                    </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" vertical={false} />
                <XAxis 
                    dataKey="name" 
                    tick={{ fontSize, fill: '#595959', fontWeight: 500 }} 
                    axisLine={{ stroke: '#d9d9d9', strokeWidth: 1 }}
                    tickLine={{ stroke: '#d9d9d9' }}
                    angle={xAxisAngle}
                    textAnchor="end"
                    height={xAxisHeight}
                    interval={xAxisInterval}
                />
                <YAxis 
                    tick={{ fontSize: yAxisFontSize, fill: '#595959', fontWeight: 500 }}
                    axisLine={{ stroke: '#d9d9d9', strokeWidth: 1 }}
                    tickLine={{ stroke: '#d9d9d9' }}
                    width={yAxisWidth}
                />
                <Tooltip 
                    content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                                <div style={{
                                    backgroundColor: '#fff',
                                    border: '1px solid #e8e8e8',
                                    borderRadius: '6px',
                                    padding: '12px 16px',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                    fontSize: '13px',
                                }}>
                                    <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: '#262626', fontSize: '14px' }}>
                                        {data.fullName}
                                    </p>
                                    <p style={{ margin: '0 0 4px 0', fontWeight: '700', color: '#262626', fontSize: '24px', lineHeight: '1.2' }}>
                                        {data.value}
                                    </p>
                                </div>
                            );
                        }
                        return null;
                    }}
                />
                <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#4085c6"
                    strokeWidth={3}
                    fill="url(#colorValue)"
                    dot={{ fill: '#4085c6', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, stroke: '#4085c6', strokeWidth: 2 }}
                />
            </AreaChart>
        </ResponsiveContainer>
    );
});

AnswersByDateChart.displayName = 'AnswersByDateChart';

export default AnswersByDateChart;

